
import { Quadrant, MessageTemplate, TriggerAssociation, SystemEvent } from './types';

export const ENV = {
  PROJECT_ID: 'priori-ai-1',
  FIREBASE_PROJECT_ID: 'priori-ai-1',
  REGION: 'us-west1',
  DEPLOYMENT_ZONE: 'us-west1-a',
  APP_URL: 'https://priori-ai.com',
  REQUIRED_CLUSTER: 'us-west1',
  MIGRATION_VERSION: 'v1.4-vanguard-auth-hardened',
  LEGACY_RELAY: 'us-west1',
  DEPLOYMENT_STATUS: 'ACTIVE_INSTANCE',
  FROM_EMAIL: 'command@priori-ai.com',
};

export const QUADRANT_CONFIG = {
  [Quadrant.DO]: {
    label: 'Reactive Handling (Q1: Crisis Management)',
    sublabel: 'Immediate Action Required',
    color: 'border-rose-500/50 bg-rose-500/5',
    iconColor: 'text-rose-400'
  },
  [Quadrant.SCHEDULE]: {
    label: 'The Strategic Moat (Q2: Deep Strategic)',
    sublabel: 'Important & Planning-Focused',
    color: 'border-indigo-500/50 bg-indigo-500/5',
    iconColor: 'text-indigo-400'
  },
  [Quadrant.DELEGATE]: {
    label: 'Operational Noise (Q3: Support Ops)',
    sublabel: 'Administrative & Support',
    color: 'border-emerald-500/50 bg-emerald-500/5',
    iconColor: 'text-emerald-400'
  },
  [Quadrant.ELIMINATE]: {
    label: 'The Parking Lot (Q4: Distractions)',
    sublabel: 'Non-Essential Activities',
    color: 'border-slate-700/50 bg-slate-900/50',
    iconColor: 'text-slate-50'
  }
};

export const CORE_TEMPLATES: MessageTemplate[] = [
  {
    id: 'WELCOME_NEW_USER',
    name: 'WELCOME_NEW_USER',
    subject: 'Welcome to Priori, {{userName}}',
    body: 'Hello {{userName}},<br/><br/>Welcome to Priori — the Execution & Clarity System.<br/><br/>Your grid is ready.<br/><br/>Start defining what matters and executing with focus.<br/><br/>Access your workspace:<br/>{{appUrl}}<br/><br/>Your operator identity:<br/>{{userEmail}}<br/><br/>Build momentum one action at a time.<br/><br/>— The Priori System'
  },
  {
    id: 'PROFILE_DELETED',
    name: 'PROFILE_DELETED',
    subject: 'Your Priori profile has been deleted',
    body: 'Hello {{userName}},<br/><br/>Your Priori profile associated with {{userEmail}} has been successfully removed.<br/><br/>If this action was taken in error, you may create a new workspace anytime.<br/><br/>Return to Priori:<br/>{{appUrl}}<br/><br/>Thank you for being part of the system.<br/><br/>— Priori'
  },
  {
    id: 'OBJECTIVE_DEPLOYED',
    name: 'OBJECTIVE_DEPLOYED',
    subject: 'New objective created: {{taskTitle}}',
    body: 'Hello {{userName}},<br/><br/>A new objective has been created in your execution grid.<br/><br/>Objective:<br/>{{taskTitle}}<br/><br/>Quadrant:<br/>{{quadrant}}<br/><br/>Open your grid:<br/>{{appUrl}}<br/><br/>Stay focused on what moves the mission forward.<br/><br/>— Priori'
  },
  {
    id: 'SUBTASK_UPDATE',
    name: 'SUBTASK_UPDATE',
    subject: 'Subtask update: {{subtaskTitle}}',
    body: 'Hello {{userName}},<br/><br/>A subtask has been updated.<br/><br/>Task:<br/>{{taskTitle}}<br/><br/>Subtask:<br/>{{subtaskTitle}}<br/><br/>Status:<br/>{{subtaskStatus}}<br/><br/>View details:<br/>{{appUrl}}<br/><br/>Execution is progress.<br/><br/>— Priori'
  },
  {
    id: 'OBJECTIVE_COMPLETED',
    name: 'OBJECTIVE_COMPLETED',
    subject: 'Objective completed: {{taskTitle}}',
    body: 'Hello {{userName}},<br/><br/>An objective has been completed.<br/><br/>Task:<br/>{{taskTitle}}<br/><br/>Quadrant:<br/>{{quadrant}}<br/><br/>Return to your grid to continue building momentum.<br/><br/>{{appUrl}}<br/><br/>Consistency compounds.<br/><br/>— Priori'
  },
  {
    id: 'PARTNER_UPDATE_NOTIF',
    name: 'PARTNER_UPDATE_NOTIF',
    subject: 'Update from your accountability partner',
    body: 'Hello {{userName}},<br/><br/>Your accountability partner has shared an update regarding:<br/><br/>{{taskTitle}}<br/><br/>Open Priori to review the latest progress.<br/><br/>{{appUrl}}<br/><br/>Stay aligned. Stay accountable.<br/><br/>— Priori'
  },
  {
    id: 'PARTNER_PULSE_REQUEST',
    name: 'PARTNER_PULSE_REQUEST',
    subject: 'Your partner requested an update',
    body: 'Hello {{userName}},<br/><br/>Your accountability partner has requested an update on:<br/><br/>{{taskTitle}}<br/><br/>Quadrant:<br/>{{quadrant}}<br/><br/>Provide an update here:<br/>{{appUrl}}<br/><br/>Or respond directly:<br/><br/>{{pulseRequestUrl}}<br/><br/>Keep the signal flowing.<br/><br/>— Priori'
  },
  {
    id: 'DAILY_PULSE_REPORT',
    name: 'DAILY_PULSE_REPORT',
    subject: 'Your Priori daily execution brief',
    body: 'Hello {{userName}},<br/><br/><strong>Today\'s Coach Insight</strong><br/>{{coachInsight}}<br/><br/>{{message}}<br/><br/>Open Priori:<br/>{{appUrl}}<br/><br/>Small consistent actions create momentum.<br/><br/>— Priori'
  },
  {
    id: 'INACTIVE_REMINDER',
    name: 'INACTIVE_REMINDER',
    subject: 'We haven\'t seen activity recently',
    body: 'Hello {{userName}},<br/><br/>Your grid has been inactive for the past seven days.<br/><br/>Return to Priori to regain momentum.<br/><br/>{{appUrl}}<br/><br/>Your future execution depends on today\'s action.<br/><br/>— Priori'
  },
  {
    id: 'STREAK_CELEBRATION',
    name: 'STREAK_CELEBRATION',
    subject: '7 day execution streak achieved',
    body: 'Hello {{userName}},<br/><br/>You\'ve maintained a seven day execution streak.<br/><br/>Consistency is the foundation of mastery.<br/><br/>Continue building your momentum.<br/><br/>{{appUrl}}<br/><br/>Keep going.<br/><br/>— Priori'
  },
  {
    id: 'INVOICE_SUCCESS',
    name: 'INVOICE_SUCCESS',
    subject: 'Payment received',
    body: 'Hello {{userName}},<br/><br/>Your payment has been successfully processed.<br/><br/>Amount:<br/>{{amount}}<br/><br/>Thank you for supporting the Priori system.<br/><br/>Continue your execution journey:<br/><br/>{{appUrl}}<br/><br/>— Priori'
  },
  {
    id: 'PAYMENT_FAILURE',
    name: 'PAYMENT_FAILURE',
    subject: 'Payment failed',
    body: 'Hello {{userName}},<br/><br/>Your recent payment attempt could not be processed.<br/><br/>Please review your billing details to restore full access.<br/><br/>Access Priori:<br/>{{appUrl}}<br/><br/>— Priori'
  },
  {
    id: 'SUBSCRIPTION_OFFBOARD',
    name: 'SUBSCRIPTION_OFFBOARD',
    subject: 'Subscription canceled',
    body: 'Hello {{userName}},<br/><br/>Your Priori subscription has been canceled.<br/><br/>Your workspace will remain accessible based on your current plan status.<br/><br/>Return anytime:<br/><br/>{{appUrl}}<br/><br/>Thank you for being part of Priori.'
  },
  {
    id: 'BROADCAST_ALERT',
    name: 'BROADCAST_ALERT',
    subject: '{{customTitle}}',
    body: 'Hello {{userName}},<br/><br/>{{customMessage}}<br/><br/>Access Priori:<br/>{{appUrl}}<br/><br/>— Priori System Broadcast'
  }
];

export const CORE_ASSOCIATIONS: TriggerAssociation[] = [
  { id: 'assoc_welcome', systemEvent: 'welcome_new_user' as SystemEvent, emailTask: 'WELCOME_NEW_USER', schedule: 'realtime', deliveryMode: 'per_user' },
  { id: 'assoc_partner_req', systemEvent: 'partner_request' as SystemEvent, emailTask: 'PARTNER_PULSE_REQUEST', schedule: 'realtime', deliveryMode: 'per_user' },
  { id: 'assoc_partner_upd', systemEvent: 'partner_update' as SystemEvent, emailTask: 'PARTNER_UPDATE_NOTIF', schedule: 'realtime', deliveryMode: 'per_user' },
  { id: 'assoc_created', systemEvent: 'created' as SystemEvent, emailTask: 'OBJECTIVE_DEPLOYED', schedule: 'realtime', deliveryMode: 'per_user' },
  { id: 'assoc_completed', systemEvent: 'completed' as SystemEvent, emailTask: 'OBJECTIVE_COMPLETED', schedule: 'realtime', deliveryMode: 'per_user' },
  { id: 'assoc_subtask', systemEvent: 'subtask' as SystemEvent, emailTask: 'SUBTASK_UPDATE', schedule: 'realtime', deliveryMode: 'per_user' },
  { id: 'assoc_daily', systemEvent: 'daily' as SystemEvent, emailTask: 'DAILY_PULSE_REPORT', schedule: 'daily', deliveryMode: 'per_user' },
  { id: 'assoc_deleted', systemEvent: 'profile_deleted' as SystemEvent, emailTask: 'PROFILE_DELETED', schedule: 'realtime', deliveryMode: 'per_user' },
  { id: 'assoc_inactive', systemEvent: 'inactivity_7_days' as any, emailTask: 'INACTIVE_REMINDER', schedule: 'daily', deliveryMode: 'per_user' },
  { id: 'assoc_streak', systemEvent: 'streak_7_day' as any, emailTask: 'STREAK_CELEBRATION', schedule: 'daily', deliveryMode: 'per_user' },
  { id: 'assoc_inv_success', systemEvent: 'invoice_paid' as any, emailTask: 'INVOICE_SUCCESS', schedule: 'realtime', deliveryMode: 'per_user' },
  { id: 'assoc_inv_fail', systemEvent: 'payment_failed' as any, emailTask: 'PAYMENT_FAILURE', schedule: 'realtime', deliveryMode: 'per_user' },
  { id: 'assoc_sub_cancel', systemEvent: 'subscription_canceled' as any, emailTask: 'SUBSCRIPTION_OFFBOARD', schedule: 'realtime', deliveryMode: 'per_user' },
  { id: 'assoc_broadcast', systemEvent: 'broadcast' as SystemEvent, emailTask: 'BROADCAST_ALERT', schedule: 'realtime', deliveryMode: 'broadcast' }
];

export const SENTINEL_DOCS = {
  TASKS: 'sentinel_tasks_init',
  USERS: 'sentinel_users_init',
  EVENTS: 'sentinel_events_init',
  ANALYTICS: 'sentinel_analytics_init',
  CONFIG: 'sentinel_config_init',
  TEMPLATES: 'sentinel_templates_init'
};